/**
 * Revenue Intelligence Dashboard
 * Sistema completo di analisi e suggerimenti pricing
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Brain, TrendingUp, TrendingDown, Sparkles, Target, Calendar,
  ChevronLeft, ChevronRight, Check, X, AlertCircle, Settings,
  Zap, Sun, Cloud, CloudRain, Thermometer, Users, Building2,
  BarChart3, Activity, RefreshCw, Play, Plus, Euro, Percent
} from 'lucide-react';
import { API } from '../../lib/api';

// ============================================================
// COMPONENTI UI
// ============================================================

// Circular Score Indicator
const ScoreCircle = ({ score, size = 'lg', label }) => {
  const radius = size === 'lg' ? 45 : size === 'md' ? 35 : 25;
  const stroke = size === 'lg' ? 8 : 6;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  
  const getColor = (s) => {
    if (s >= 80) return '#10b981';
    if (s >= 60) return '#f59e0b';
    if (s >= 40) return '#f97316';
    return '#ef4444';
  };
  
  return (
    <div className="relative flex flex-col items-center">
      <svg 
        width={(radius + stroke) * 2} 
        height={(radius + stroke) * 2} 
        className="transform -rotate-90"
      >
        <circle
          cx={radius + stroke}
          cy={radius + stroke}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={stroke}
        />
        <circle
          cx={radius + stroke}
          cy={radius + stroke}
          r={radius}
          fill="none"
          stroke={getColor(score)}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`font-bold ${size === 'lg' ? 'text-3xl' : size === 'md' ? 'text-xl' : 'text-lg'}`}>
          {Math.round(score)}
        </span>
        {label && <span className="text-xs text-gray-500">{label}</span>}
      </div>
    </div>
  );
};

// Factor Bar
const FactorBar = ({ factor, score, weight, explanation }) => {
  const getFactorIcon = (f) => {
    switch(f) {
      case 'demand': return <Users className="w-4 h-4" />;
      case 'seasonality': return <Sun className="w-4 h-4" />;
      case 'events': return <Calendar className="w-4 h-4" />;
      case 'weather': return <Cloud className="w-4 h-4" />;
      case 'occupancy': return <Building2 className="w-4 h-4" />;
      case 'lead_time': return <Activity className="w-4 h-4" />;
      case 'history': return <BarChart3 className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };
  
  const getFactorLabel = (f) => {
    const labels = {
      demand: 'Domanda',
      seasonality: 'Stagionalità',
      events: 'Eventi',
      weather: 'Meteo',
      occupancy: 'Occupazione',
      lead_time: 'Lead Time',
      history: 'Storico',
      competitor: 'Competitor'
    };
    return labels[f] || f;
  };
  
  const getBarColor = (s) => {
    if (s >= 70) return 'bg-emerald-500';
    if (s >= 50) return 'bg-amber-500';
    return 'bg-rose-500';
  };
  
  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">{getFactorIcon(factor)}</span>
          <span className="text-sm font-medium text-gray-700">{getFactorLabel(factor)}</span>
          <span className="text-xs text-gray-400">({weight}%)</span>
        </div>
        <span className="text-sm font-bold text-gray-800">{Math.round(score)}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getBarColor(score)} rounded-full transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">{explanation}</p>
    </div>
  );
};

// Price Suggestion Card
const SuggestionCard = ({ suggestion, onApply, applying }) => {
  const changePercent = suggestion.price_change_percent;
  const isIncrease = changePercent > 0;
  const isDecrease = changePercent < 0;
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-200 text-xs font-medium uppercase tracking-wide">Suggerimento AI</p>
            <h3 className="text-xl font-bold">{suggestion.unit_name}</h3>
            <p className="text-indigo-200 text-sm">{new Date(suggestion.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          <ScoreCircle score={suggestion.revenue_score} size="md" />
        </div>
      </div>
      
      {/* Price Comparison */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="flex items-center justify-between">
          <div className="text-center">
            <p className="text-xs text-gray-500">Attuale</p>
            <p className="text-xl font-bold text-gray-400 line-through">€{suggestion.current_price}</p>
          </div>
          
          <div className="flex flex-col items-center">
            <span className={`text-sm font-bold px-2 py-1 rounded-full ${
              isIncrease ? 'bg-emerald-100 text-emerald-700' : 
              isDecrease ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {isIncrease ? '+' : ''}{changePercent.toFixed(1)}%
            </span>
            {isIncrease ? <TrendingUp className="w-4 h-4 text-emerald-500 mt-1" /> : 
             isDecrease ? <TrendingDown className="w-4 h-4 text-rose-500 mt-1" /> : null}
          </div>
          
          <div className="text-center">
            <p className="text-xs text-gray-500">Suggerito</p>
            <p className="text-2xl font-bold text-indigo-600">€{suggestion.suggested_price}</p>
          </div>
        </div>
      </div>
      
      {/* Confidence */}
      <div className="px-4 py-3 border-b">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Confidenza AI</span>
          <div className="flex items-center gap-2">
            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  suggestion.confidence >= 80 ? 'bg-emerald-500' :
                  suggestion.confidence >= 60 ? 'bg-amber-500' : 'bg-rose-500'
                }`}
                style={{ width: `${suggestion.confidence}%` }}
              />
            </div>
            <span className="text-sm font-bold">{suggestion.confidence}%</span>
          </div>
        </div>
      </div>
      
      {/* Motivations */}
      <div className="p-4 border-b">
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Motivazioni</p>
        <div className="space-y-1">
          {suggestion.motivations.map((m, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
              {m.startsWith('✔') ? (
                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              )}
              <span>{m.replace('✔ ', '').replace('⚠ ', '')}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Limits */}
      <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500">
        Limiti: €{suggestion.limits.min} - €{suggestion.limits.max} | Strategia: {suggestion.strategy}
      </div>
      
      {/* Actions */}
      <div className="p-4">
        <Button 
          onClick={() => onApply(suggestion)}
          disabled={applying}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {applying ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Check className="w-4 h-4 mr-2" />
          )}
          Applica €{suggestion.suggested_price}
        </Button>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function RevenueIntelligence() {
  const { token } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [viewMode, setViewMode] = useState('single'); // single | period
  
  // Results
  const [currentScore, setCurrentScore] = useState(null);
  const [currentSuggestion, setCurrentSuggestion] = useState(null);
  const [periodSuggestions, setPeriodSuggestions] = useState(null);
  
  // Strategy
  const [strategy, setStrategy] = useState({ strategy: 'balanced' });
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  
  // Events
  const [events, setEvents] = useState([]);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    name: '',
    date_start: '',
    date_end: '',
    category: 'local',
    importance: 3,
    impact_percent: 15
  });

  // Load initial data
  useEffect(() => {
    if (token) {
      loadInitialData();
    }
  }, [token]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, dashboardRes, unitsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/admin/revenue/status`, { headers }),
        axios.get(`${API}/admin/revenue/dashboard`, { headers }),
        axios.get(`${API}/admin/units`, { headers }),
        axios.get(`${API}/admin/revenue/events`, { headers })
      ]);
      
      setStatus(statusRes.data);
      setDashboard(dashboardRes.data);
      setUnits(unitsRes.data || []);
      setEvents(eventsRes.data?.events || []);
      
      // Select first unit by default
      if (unitsRes.data?.length > 0) {
        setSelectedUnit(unitsRes.data[0].id);
      }
      
      // Set default date range (next 30 days)
      const today = new Date();
      const in30Days = new Date(today);
      in30Days.setDate(in30Days.getDate() + 30);
      setDateRange({
        from: today.toISOString().split('T')[0],
        to: in30Days.toISOString().split('T')[0]
      });
      
    } catch (err) {
      toast.error('Errore caricamento dati');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load strategy for unit
  useEffect(() => {
    if (selectedUnit && token) {
      loadStrategy();
    }
  }, [selectedUnit, token]);

  const loadStrategy = async () => {
    try {
      const res = await axios.get(`${API}/admin/revenue/strategy/${selectedUnit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategy(res.data);
    } catch (err) {
      console.error('Error loading strategy:', err);
    }
  };

  // Analyze single date
  const analyzeSingleDate = async () => {
    if (!selectedUnit || !selectedDate) {
      toast.error('Seleziona unità e data');
      return;
    }
    
    setAnalyzing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [scoreRes, suggestionRes] = await Promise.all([
        axios.get(`${API}/admin/revenue/score/${selectedUnit}/${selectedDate}`, { headers }),
        axios.get(`${API}/admin/revenue/suggestion/${selectedUnit}/${selectedDate}`, { headers })
      ]);
      
      setCurrentScore(scoreRes.data);
      setCurrentSuggestion(suggestionRes.data);
      toast.success('Analisi completata');
      
    } catch (err) {
      toast.error('Errore durante l\'analisi');
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  };

  // Analyze period
  const analyzePeriod = async () => {
    if (!selectedUnit || !dateRange.from || !dateRange.to) {
      toast.error('Seleziona unità e periodo');
      return;
    }
    
    setAnalyzing(true);
    try {
      const res = await axios.get(`${API}/admin/revenue/suggestions/${selectedUnit}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date_from: dateRange.from, date_to: dateRange.to }
      });
      
      setPeriodSuggestions(res.data);
      toast.success(`${res.data.suggestions.length} giorni analizzati`);
      
    } catch (err) {
      toast.error('Errore durante l\'analisi');
      console.error(err);
    } finally {
      setAnalyzing(false);
    }
  };

  // Apply suggestion
  const applySuggestion = async (suggestion) => {
    setApplying(true);
    try {
      await axios.post(`${API}/admin/revenue/apply`, {
        unit_id: suggestion.unit_id,
        date: suggestion.date,
        suggested_price: suggestion.suggested_price,
        sync_beds24: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Prezzo €${suggestion.suggested_price} applicato per ${suggestion.date}`);
      
    } catch (err) {
      toast.error('Errore applicazione prezzo');
      console.error(err);
    } finally {
      setApplying(false);
    }
  };

  // Update strategy
  const updateStrategy = async (newStrategy) => {
    try {
      await axios.put(`${API}/admin/revenue/strategy`, {
        unit_id: selectedUnit,
        strategy: newStrategy
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStrategy({ ...strategy, strategy: newStrategy });
      toast.success('Strategia aggiornata');
      setStrategyDialogOpen(false);
      
    } catch (err) {
      toast.error('Errore aggiornamento strategia');
    }
  };

  // Create event
  const createEvent = async () => {
    try {
      await axios.post(`${API}/admin/revenue/events`, newEvent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Evento creato');
      setEventDialogOpen(false);
      setNewEvent({
        name: '',
        date_start: '',
        date_end: '',
        category: 'local',
        importance: 3,
        impact_percent: 15
      });
      
      // Reload events
      const res = await axios.get(`${API}/admin/revenue/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data?.events || []);
      
    } catch (err) {
      toast.error('Errore creazione evento');
    }
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <Brain className="w-12 h-12 text-indigo-500 mx-auto mb-3 animate-pulse" />
            <p className="text-gray-500">Caricamento Revenue Intelligence...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6 bg-gray-50 min-h-screen">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Brain className="w-7 h-7 text-indigo-600" />
              Revenue Intelligence
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Analisi e suggerimenti pricing basati su dati
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setStrategyDialogOpen(true)}
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Strategia
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setEventDialogOpen(true)}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Evento
            </Button>
          </div>
        </div>

        {/* Status Bar */}
        {status && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className={`p-3 rounded-xl ${status.available ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
              <div className="flex items-center gap-2">
                {status.available ? (
                  <Check className="w-5 h-5 text-emerald-600" />
                ) : (
                  <X className="w-5 h-5 text-rose-600" />
                )}
                <span className="text-sm font-medium">Engine</span>
              </div>
            </div>
            <div className={`p-3 rounded-xl ${status.features?.weather ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Cloud className={`w-5 h-5 ${status.features?.weather ? 'text-emerald-600' : 'text-gray-400'}`} />
                <span className="text-sm font-medium">Meteo API</span>
              </div>
            </div>
            <div className={`p-3 rounded-xl ${status.features?.beds24_sync ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Zap className={`w-5 h-5 ${status.features?.beds24_sync ? 'text-emerald-600' : 'text-gray-400'}`} />
                <span className="text-sm font-medium">Beds24 Sync</span>
              </div>
            </div>
            <div className="p-3 rounded-xl bg-indigo-50 border border-indigo-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <span className="text-sm font-medium">{events.length} Eventi</span>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Controls */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              Analizza Prezzi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Unit Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Struttura</Label>
                <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona struttura" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.nome} (€{unit.prezzo_base})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Modalità</Label>
                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Giorno singolo</SelectItem>
                    <SelectItem value="period">Periodo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {viewMode === 'single' ? (
                <div>
                  <Label>Data</Label>
                  <Input 
                    type="date" 
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Da</Label>
                    <Input 
                      type="date" 
                      value={dateRange.from}
                      onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>A</Label>
                    <Input 
                      type="date" 
                      value={dateRange.to}
                      onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Strategy Badge */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Strategia attiva:</span>
              <Badge className={
                strategy.strategy === 'aggressive' ? 'bg-rose-100 text-rose-700' :
                strategy.strategy === 'conservative' ? 'bg-emerald-100 text-emerald-700' :
                'bg-indigo-100 text-indigo-700'
              }>
                {strategy.strategy === 'aggressive' ? '🔴 Aggressivo' :
                 strategy.strategy === 'conservative' ? '🟢 Conservativo' :
                 '🔵 Bilanciato'}
              </Badge>
            </div>
            
            {/* Analyze Button */}
            <Button 
              onClick={viewMode === 'single' ? analyzeSingleDate : analyzePeriod}
              disabled={analyzing || !selectedUnit}
              className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700"
            >
              {analyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analisi in corso...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Analizza
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results - Single Date */}
        {viewMode === 'single' && currentScore && currentSuggestion && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Score Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-indigo-500" />
                    Revenue Score
                  </span>
                  <ScoreCircle score={currentScore.total_score} size="lg" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {currentScore.factors.map((factor, i) => (
                    <FactorBar 
                      key={i}
                      factor={factor.factor}
                      score={factor.score}
                      weight={factor.weight}
                      explanation={factor.explanation}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Suggestion */}
            <SuggestionCard 
              suggestion={currentSuggestion}
              onApply={applySuggestion}
              applying={applying}
            />
          </div>
        )}

        {/* Results - Period */}
        {viewMode === 'period' && periodSuggestions && (
          <div className="space-y-4">
            {/* Summary */}
            <Card>
              <CardContent className="py-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-indigo-600">{periodSuggestions.summary.days_analyzed}</p>
                    <p className="text-xs text-gray-500">Giorni analizzati</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-600">{periodSuggestions.summary.avg_revenue_score}</p>
                    <p className="text-xs text-gray-500">Score medio</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${periodSuggestions.summary.avg_price_change_percent >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {periodSuggestions.summary.avg_price_change_percent >= 0 ? '+' : ''}{periodSuggestions.summary.avg_price_change_percent}%
                    </p>
                    <p className="text-xs text-gray-500">Variazione media</p>
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${periodSuggestions.summary.total_potential_revenue >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      €{periodSuggestions.summary.total_potential_revenue.toLocaleString('it-IT')}
                    </p>
                    <p className="text-xs text-gray-500">Potenziale</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Suggestions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {periodSuggestions.suggestions.slice(0, 30).map((sugg, i) => (
                <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-gray-800">
                        {new Date(sugg.date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-xs text-gray-500">{sugg.unit_name}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      sugg.revenue_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                      sugg.revenue_score >= 50 ? 'bg-amber-100 text-amber-700' :
                      'bg-rose-100 text-rose-700'
                    }`}>
                      {Math.round(sugg.revenue_score)}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-gray-400 line-through text-sm">€{sugg.current_price}</span>
                      <span className="text-xl font-bold text-indigo-600 ml-2">€{sugg.suggested_price}</span>
                    </div>
                    <span className={`text-sm font-bold ${
                      sugg.price_change_percent >= 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {sugg.price_change_percent >= 0 ? '+' : ''}{sugg.price_change_percent.toFixed(0)}%
                    </span>
                  </div>
                  
                  <Button 
                    size="sm"
                    variant="outline"
                    className="w-full mt-3"
                    onClick={() => applySuggestion(sugg)}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Applica
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Events List */}
        {events.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-500" />
                Prossimi Eventi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {events.slice(0, 5).map((event, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="text-lg">
                        {'⭐'.repeat(Math.min(event.importance, 5))}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{event.name}</p>
                        <p className="text-xs text-gray-500">
                          {event.date_start} → {event.date_end}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-indigo-100 text-indigo-700">
                      +{event.impact_percent}%
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strategy Dialog */}
        <Dialog open={strategyDialogOpen} onOpenChange={setStrategyDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Configura Strategia Revenue</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-500">
                Seleziona la strategia di pricing per questa struttura:
              </p>
              
              <div className="space-y-3">
                <button 
                  onClick={() => updateStrategy('conservative')}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    strategy.strategy === 'conservative' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🟢</span>
                    <div>
                      <p className="font-semibold text-gray-800">Conservativo</p>
                      <p className="text-sm text-gray-500">Massimizza occupazione, prezzi stabili</p>
                    </div>
                  </div>
                </button>
                
                <button 
                  onClick={() => updateStrategy('balanced')}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    strategy.strategy === 'balanced' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🔵</span>
                    <div>
                      <p className="font-semibold text-gray-800">Bilanciato</p>
                      <p className="text-sm text-gray-500">Equilibrio tra occupazione e ricavi</p>
                    </div>
                  </div>
                </button>
                
                <button 
                  onClick={() => updateStrategy('aggressive')}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    strategy.strategy === 'aggressive' ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🔴</span>
                    <div>
                      <p className="font-semibold text-gray-800">Aggressivo</p>
                      <p className="text-sm text-gray-500">Massimizza ADR, prezzi dinamici</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Event Dialog */}
        <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuovo Evento</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nome Evento</Label>
                <Input 
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                  placeholder="Es: Concerto ai Templi"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data Inizio</Label>
                  <Input 
                    type="date"
                    value={newEvent.date_start}
                    onChange={(e) => setNewEvent({ ...newEvent, date_start: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Data Fine</Label>
                  <Input 
                    type="date"
                    value={newEvent.date_end}
                    onChange={(e) => setNewEvent({ ...newEvent, date_end: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Importanza (1-5)</Label>
                  <Select 
                    value={String(newEvent.importance)} 
                    onValueChange={(v) => setNewEvent({ ...newEvent, importance: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">⭐ Basso</SelectItem>
                      <SelectItem value="2">⭐⭐ Medio-basso</SelectItem>
                      <SelectItem value="3">⭐⭐⭐ Medio</SelectItem>
                      <SelectItem value="4">⭐⭐⭐⭐ Alto</SelectItem>
                      <SelectItem value="5">⭐⭐⭐⭐⭐ Altissimo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Impatto Domanda %</Label>
                  <Input 
                    type="number"
                    value={newEvent.impact_percent}
                    onChange={(e) => setNewEvent({ ...newEvent, impact_percent: parseFloat(e.target.value) })}
                    placeholder="15"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEventDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={createEvent} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Crea Evento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </AdminLayout>
  );
}
